import React, { useState } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  getReviewTemplateCategory,
  sanitizeReviewLabel,
  transformReviewOption,
} from '~/account/utils/reviews';
import LoadingSpinner from '~/shared/components/LoadingSpinner';
import featureNames from '~/shared/constants/featureFlags';
import useHasFeature from '~/shared/utils/useHasFeature';
import { AssetContainer, HTMLAsset } from '~/techstyle-shared/react-assets';
import {
  Button,
  mobile,
  desktop,
  Modal as BaseModal,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import BasketItem from '../BasketItem';
import ReviewOptionBar from '../ReviewOptionBar';
import ReviewOptionImage from '../ReviewOptionImage';
import ReviewOptionRadio from '../ReviewOptionRadio';
import ReviewOptionStars from '../ReviewOptionStars';
import ReviewOptionText from '../ReviewOptionText';

const ReviewOptionSpacer = styled.span`
  display: block;
  height: ${({ theme }) => theme.spacing.sizes.pixels.xxxl}px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.palette.white};
  padding: 32px;
  border-radius: ${({ theme }) => theme.borderRadiusCard};
  font-weight: 450;
`;

const ListWrapper = styled.div`
  width: 100%;
`;

const WriteReviewProductHeader = styled(BasketItem).attrs({
  membership: {},
  showStock: false,
  showPrice: false,
  showQuantity: false,
  showQuantitySelector: false,
  showRemoveIcon: false,
  linkToProduct: false,
})`
  border-bottom: 0;
  margin-bottom: 12px;
  padding-top: 0;
  padding-bottom: 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-top: ${({ theme }) => theme.sizes.md}px;
  gap: ${({ theme }) => theme.sizes.lg}px;

  ${({ theme }) => theme.reviewStyles.ctaButtonContainerStyle}

  ${mobile`
    gap: ${({ theme }) => theme.sizes.sm}px;
  `}
`;

const ButtonBorderedBlack = styled(Button)`
  flex: 1;
  height: 50px;
  background-color: transparent;
  border: solid 3px ${({ theme }) => theme.colors.palette.black};
  font-weight: bold;
  outline: none;
  &:active,
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.palette.black};
    span {
      color: ${({ theme }) => theme.colors.textLight};
    }
  }
  span {
    color: ${({ theme }) => theme.colors.textDefault};
  }
`;

const ButtonDisplayCancel = styled(ButtonBorderedBlack)`
  ${({ theme }) => theme.reviewStyles.ctaButtonStyle};
  ${({ theme }) => theme.button.borderRadius};
`;

const ButtonSolidBlack = styled(Button)`
  flex: 1;
  height: 50px;
  font-weight: bold;
  outline: none;
  color: ${({ theme }) => theme.colors.reviewBrowserSubmit};
  background-color: ${({ theme }) => theme.colors.textDefault};
`;

const ButtonDisplaySubmit = styled(ButtonSolidBlack)`
  background-color: ${({ theme, disabled }) =>
    !disabled ? theme.colors.palette.black : theme.colors.palette.black};
  border: solid 3px ${({ theme }) => theme.colors.palette.black};
  color: ${({ theme, disabled }) =>
    !disabled
      ? theme.colors.reviewBrowserSubmit
      : theme.colors.reviewBrowserSubmitDisabled};
  cursor: ${({ disabled }) => (!disabled ? 'pointer' : null)};

  ${({ theme }) => theme.reviewStyles.ctaButtonStyle};
  ${({ theme }) => theme.button.borderRadius};
`;

const TermsAsset = styled(HTMLAsset)`
  margin-bottom: ${(props) => props.theme.spacing.moderate}px;
`;

const SpinnerIndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  margin-top: 10px;
`;

const dialogStyle = css`
  ${({
    theme: {
      colors,
      spacing: { sizes },
    },
  }) => `
    border-radius: ${sizes.pixels.s}px;
    background-color: ${colors.palette.white};
    margin: ${sizes.pixels.m}px;
    margin-top: auto;
    padding: ${sizes.pixels.m}px;
    h3 {
      margin-top: ${sizes.pixels.s}px;
      text-align: center;
      font-weight: 600;
      font-size: 20px;
    }
    p {
      margin: ${sizes.pixels.s}px;
      margin-bottom: 50px;
      font-size: 16px;
      line-height: 20px;
    }
    button {
      width: 100%;
    }
    button:last-of-type {
      margin: ${sizes.pixels.xs}px auto;
    }
  `}
  width: 100%;
  ${desktop`
    margin-bottom: auto;
    max-width: 450px;
    > div {
      display: flex;
      justify-content: center;
    }
    button {
      max-width: 180px;
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 12px;
    }
    button:last-of-type {
      margin: 0;
      margin-right: 20px;
      order: -1;
    }
  `};
`;

const Modal = styled(BaseModal).attrs({
  dialogStyle,
})``;

const ErrorMessage = styled.p`
  font-family: ${({ theme }) => theme.colors.error};
`;

const renderOption = ({
  allowUserUploadedReviewPhotos,
  handleChange,
  handlePhotoChange,
  isError,
  option: reviewOption,
  reviewTemplateId,
}) => {
  if (!reviewOption) {
    return null;
  }

  const option = transformReviewOption(reviewOption);

  const title = option.description || option.label;
  const templateCategory = getReviewTemplateCategory(reviewTemplateId);
  const reviewLabel = sanitizeReviewLabel(option.label);

  const rbKey = `site_account_reviews.review_${reviewLabel}_${templateCategory}`;

  if (option.reviewFormControlTypeLabel === 'star') {
    return (
      <ReviewOptionStars
        isError={isError && option.required}
        isRequired={option.isRequired}
        key={option.reviewTemplateFieldId}
        onChange={handleChange}
        option={option}
        rbKey={rbKey}
        title={title}
      />
    );
  } else if (option.reviewFormControlTypeLabel === 'slider') {
    if (!option.answers || option.answers.length === 0) {
      return null;
    }

    const { answers } = option;

    const sanitizedOptions = answers.map(
      ({
        reviewTemplateFieldAnswerId: value,
        reviewTemplateFieldId,
        label,
      }) => {
        return {
          value,
          reviewTemplateFieldId,
          label,
        };
      }
    );

    return (
      <ReviewOptionBar
        highestRatingText={answers[option.answers.length - 1].label}
        isRequired={option.required}
        key={option.reviewTemplateFieldId}
        label={option.label}
        lowestRatingText={answers[0].label}
        onChange={handleChange}
        options={sanitizedOptions}
        rbKey={rbKey}
        title={title}
      />
    );
  } else if (option.reviewFormControlTypeLabel === 'radio') {
    return (
      <ReviewOptionRadio
        isError={isError && option.required}
        key={option.reviewTemplateFieldId}
        label={reviewLabel}
        onChange={handleChange}
        option={option}
        rbKey={rbKey}
        title={title}
      />
    );
  } else if (
    option.reviewFormControlTypeLabel === 'text' ||
    option.reviewFormControlTypeLabel === 'textarea'
  ) {
    return (
      <ReviewOptionText
        isTextArea={option.reviewFormControlTypeLabel === 'textarea'}
        isError={isError && option.required}
        key={option.reviewTemplateFieldId}
        onChange={handleChange}
        option={option}
        rbKey={rbKey}
        title={title}
      />
    );
  } else if (option.reviewFormControlTypeLabel === 'attachment image') {
    return allowUserUploadedReviewPhotos ? (
      <ReviewOptionImage
        isError={isError && option.required}
        key={option.reviewTemplateFieldId}
        onChange={handlePhotoChange}
        option={option}
      />
    ) : null;
  }
};

renderOption.propTypes = {
  allowUserUploadedReviewPhotos: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handlePhotoChange: PropTypes.func,
  isError: PropTypes.bool,
  option: PropTypes.object.isRequired,
  reviewTemplateId: PropTypes.string,
};

const ReviewBrowser = ({
  handleChange,
  product,
  handleCancel,
  handlePhotoChange,
  handleProcessReview,
  isDisabled,
  isSuccessful,
  isEmpty,
  isError,
  options,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const { pathname } = useRouter();
  const allowUserUploadedReviewPhotos = useHasFeature(
    featureNames.ALLOW_USER_UPLOADED_REVIEW_PHOTOS
  );

  return (
    <Wrapper>
      <Modal title="cancel-confirmation-modal" isOpen={showModal}>
        <h3>
          <FormattedMessage
            defaultMessage="Cancel Review"
            id="site_account_reviews.cancel_review_modal_title"
          />
        </h3>
        <p>
          <FormattedMessage
            defaultMessage="You haven’t finished your review yet. Do you want to leave without finishing it?"
            id="site_account_reviews.cancel_review_modal_message"
          />
        </p>
        <div>
          <ButtonSolidBlack
            onClick={() => {
              setShowModal(false);
            }}
          >
            <FormattedMessage
              defaultMessage="Continue Review"
              id="site_account_reviews.continue_review"
            />
          </ButtonSolidBlack>
          <ButtonBorderedBlack
            secondary
            onClick={() => {
              handleCancel();
            }}
          >
            <FormattedMessage
              defaultMessage="Discard Review"
              id="site_account_reviews.discard_review"
            />
          </ButtonBorderedBlack>
        </div>
      </Modal>
      <ListWrapper>
        <WriteReviewProductHeader
          product={product}
          pSource="my_account_reviews"
          noLink={pathname.includes('write-review')}
        />
        {error !== '' && <ErrorMessage>{error}</ErrorMessage>}

        {options[0]?.fields?.map((field, idx) => {
          return (
            <div key={idx}>
              {idx === 0 && <ReviewOptionSpacer />}
              {renderOption({
                option: field,
                handleChange,
                handlePhotoChange,
                isError,
                allowUserUploadedReviewPhotos,
                reviewTemplateId: options[0]?.reviewTemplateId,
              })}
              {idx < options[0].fields.length - 1 && <ReviewOptionSpacer />}
            </div>
          );
        })}

        <AssetContainer name="review_sweepstakes_t_and_c">
          {(asset) => {
            if (!asset.data) {
              return null;
            }

            return <TermsAsset asset={asset.data} />;
          }}
        </AssetContainer>
        <SpinnerIndicatorWrapper>
          {isSuccessful && <LoadingSpinner />}
        </SpinnerIndicatorWrapper>
        <ButtonWrapper>
          <ButtonDisplaySubmit
            variant="primary"
            disabled={!isDisabled}
            onClick={() => {
              if (isDisabled) {
                try {
                  handleProcessReview();
                } catch (err) {
                  setError(err);
                }
              }
            }}
          >
            {isSuccessful ? (
              <FormattedMessage
                defaultMessage="THANK YOU.."
                id="site_account_reviews.thankyou"
              />
            ) : (
              <FormattedMessage
                defaultMessage="SUBMIT"
                id="site_account_reviews.submit"
              />
            )}
          </ButtonDisplaySubmit>
          <ButtonDisplayCancel
            onClick={() => (isEmpty ? handleCancel() : setShowModal(true))}
            variant="secondary"
          >
            <FormattedMessage
              defaultMessage="CANCEL"
              id="site_account_reviews.cancel"
            />
          </ButtonDisplayCancel>
        </ButtonWrapper>
      </ListWrapper>
    </Wrapper>
  );
};

ReviewBrowser.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handlePhotoChange: PropTypes.func.isRequired,
  handleProcessReview: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isEmpty: PropTypes.bool,
  isError: PropTypes.bool,
  isSuccessful: PropTypes.bool,
  options: PropTypes.array,
  product: PropTypes.object.isRequired,
};

export default ReviewBrowser;
